/** The Today cockpit's reducer: readiness blockers, opportunities, incidents (U016-U020; V08-A10). */
import type {
  ExposureRow,
  IncidentRow,
  OpportunityRow,
  PhaseRow,
  ReadinessRow,
  StrategyRow,
  TodayRow,
} from "../../contracts/views.ts";
import type { ViewState } from "../../app/state.ts";

export interface Blocker {
  readonly item: string;
  readonly label: string;
  readonly state: ReadinessRow["state"];
  readonly scope: string;
  readonly reason: string;
  readonly evidenceRef: string;
}

export interface TodayModel {
  readonly strategy: StrategyRow | null;
  readonly readiness: readonly ReadinessRow[];
  readonly blockers: readonly Blocker[];
  readonly phase: PhaseRow | null;
  readonly opportunities: readonly OpportunityRow[];
  readonly exposure: ExposureRow | null;
  readonly incidents: readonly IncidentRow[];
}

/** Fold the rows into the cockpit's panels; a prerequisite that is not READY becomes a scoped blocker. */
export function reduceToday(rows: readonly TodayRow[]): TodayModel {
  const readiness = rows.filter((row): row is ReadinessRow => row.kind === "readiness");
  return {
    strategy: rows.find((row): row is StrategyRow => row.kind === "strategy") ?? null,
    readiness,
    blockers: readiness
      .filter((row) => row.state !== "READY")
      .map((row) => ({
        item: row.item,
        label: row.label,
        state: row.state,
        scope: row.scope,
        reason: row.reason ?? `${row.label} is ${row.state.toLowerCase()}`,
        evidenceRef: row.evidenceRef,
      })),
    phase: rows.find((row): row is PhaseRow => row.kind === "phase") ?? null,
    opportunities: rows.filter((row): row is OpportunityRow => row.kind === "opportunity"),
    exposure: rows.find((row): row is ExposureRow => row.kind === "exposure") ?? null,
    incidents: rows.filter((row): row is IncidentRow => row.kind === "incident"),
  };
}

export interface EntryBlock {
  readonly blocked: boolean;
  readonly reasons: readonly { readonly reason: string; readonly scope: string }[];
}

/** Whether new entries are blocked, and why: a blocked prerequisite or an engaged safety latch (U003). */
export function entryBlock(model: TodayModel): EntryBlock {
  const reasons = [
    ...model.blockers
      .filter((blocker) => blocker.state === "BLOCKED")
      .map((blocker) => ({ reason: blocker.reason, scope: blocker.scope })),
    ...model.incidents
      .filter((incident) => incident.safetyLatch === "ENGAGED")
      .map((incident) => ({ reason: `Safety latch engaged: ${incident.title}`, scope: "ACCOUNT" })),
  ];
  return { blocked: reasons.length > 0, reasons };
}

/** The opportunities panel's state: missing inputs never read as an empty, healthy market (U018). */
export function opportunitiesState(model: TodayModel): ViewState {
  if (model.opportunities.some((row) => row.status === "ACTIONABLE")) {
    return { kind: "ready" };
  }
  if (model.opportunities.length === 0 && model.blockers.length > 0) {
    const labels = model.blockers.map((blocker) => blocker.label).join(", ");
    return { kind: "unavailable", reason: `prerequisites are not ready (${labels})` };
  }
  if (model.opportunities.length === 0) {
    return { kind: "no-qualifying", reason: "no listing passed the gates" };
  }
  return {
    kind: "no-qualifying",
    reason: `${String(model.opportunities.length)} listing(s) evaluated, each NO_TRADE with a stated reason`,
  };
}
